import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';

import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react-v3';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { MetricStorePageHeaderWithCSV } from 'src/components/metricstore/components';
import { ErrorAlert } from 'src/components/resourcesmanager/components';
import { EmptyState } from 'src/commons/EmptyState';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import {
  getColumnDefinitionCollectionView,
  handleRefresh,
} from 'src/components/metricstore/metriccollection/metricCollectionUtils';

export const MetricTableView = ({ props, viewType, columnCount }) => {
  const [error, setError] = useState(undefined);
  const [allMetricItems, setAllMetricItems] = useState([]);
  const [loadingTableData, setLoadingTableData] = useState(false);
  const tableMessage = 'No metric collections';
  const [metricData, setMetricData] = useState<string[]>([]);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 300,
  });

  const [columnDefinitions, csvColumnDefinition] = useMemo(() => {
    if (allMetricItems.length > 0) {
      return getColumnDefinitionCollectionView(metricData);
    }
    console.log('No data found hence no processing is required..');
    return [[], []];
  }, [allMetricItems.length, metricData]);
  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    refreshData();
  }, []);

  const refreshData = async () => {
    await handleRefresh(
      props.match.params.id,
      setMetricData,
      setLoadingTableData,
      setAllMetricItems,
      setError,
      viewType,
      columnCount,
    );
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(allMetricItems, {
    filtering: {},
    pagination: { pageSize: preferences.pageSize },
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  const refForCSVDownloader: React.RefObject<any> = React.createRef();

  const onClickDownload = () => {
    refForCSVDownloader.current.handleClick();
  };

  return (
    <>
      {ErrorAlert(error)}

      <Table
        {...collectionProps}
        loadingText='Loading metric collections catalog...'
        loading={loadingTableData}
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        stickyColumns={{ first: 1 }}
        header={
          <>
            <MetricStorePageHeaderWithCSV
              buttons={[
                {
                  text: '',
                  icon: 'refresh',
                  onItemClick: refreshData,
                },
              ]}
              counter={`(${items.length})`}
              subheader={'Metric collections catalog'}
              rowItems={allMetricItems}
              columnDefinition={csvColumnDefinition}
              refForCSVDownloader={refForCSVDownloader}
              onClickDownload={onClickDownload}
              fileName={'view.csv'}
            />
          </>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringAriaLabel='Search metric collections'
            filteringPlaceholder='Search metric collections'
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        empty={<EmptyState title={tableMessage} subtitle='No metric collections to display' />}
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={largePageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
      />
    </>
  );
};
